// Typed product-analytics event contract; use track() rather than posthog.capture() directly.
import posthog from "posthog-js";
import type { ScanIntensity } from "@/api/scans";
import type { IdentityAuthMethod, OAuthProvisioning } from "@/api/identities";
import type { IssueSeverity } from "@/api/issues";
import type { ScanTab } from "@/stores/scan-tabs";

type VerificationMethod = "dns" | "file";
type BillingInterval = "month" | "year";
type PaidTier = "pro" | "team";
type PortalFlow = "manage" | "change_plan" | "cancel";
type IssueStatusAction = "resolve" | "reopen" | "suppress" | "unsuppress";
type FilterList = "issues" | "checks" | "findings";
type ServerTab = "scans" | "credentials" | "settings" | "issues";
type IssueTab = "overview" | "findings";

export type AnalyticsEvent =
  // Activation funnel
  | { event: "server_created" }
  | { event: "identity_added"; auth_method: IdentityAuthMethod; provisioning?: OAuthProvisioning }
  | { event: "oauth_discovered"; recommended: OAuthProvisioning }
  | {
      event: "scan_started";
      intensity: ScanIntensity;
      identity_count: number;
      adversarial_opt_in: boolean;
      source: "dashboard";
    }
  | { event: "scan_aborted" }
  | { event: "scan_deleted" }
  | { event: "scan_rerun"; intensity: ScanIntensity }
  | { event: "ownership_verification_started"; method: VerificationMethod }
  | { event: "report_pdf_downloaded" }
  | { event: "events_log_downloaded" }
  // Monetization funnel
  | { event: "billing_interval_toggled"; interval: BillingInterval }
  | { event: "checkout_started"; tier: PaidTier; interval: BillingInterval }
  | { event: "billing_portal_opened"; flow: PortalFlow }
  | { event: "enterprise_contact_clicked" }
  // Engagement — "what sections users open"
  | { event: "scan_tab_viewed"; tab: ScanTab }
  | { event: "server_tab_viewed"; tab: ServerTab }
  | { event: "issue_tab_viewed"; tab: IssueTab }
  | { event: "check_row_expanded"; family: string }
  | { event: "check_family_toggled"; family: string; expanded: boolean }
  | { event: "finding_selected"; severity: IssueSeverity }
  | { event: "issue_status_changed"; action: IssueStatusAction; severity: IssueSeverity }
  | { event: "list_filtered"; list: FilterList; filter: string; value: string }
  // Public demo scan (unauthenticated)
  | { event: "public_scan_viewed" }
  | { event: "public_scan_cta_clicked" }
  // Team / CLI
  | { event: "member_invited"; role: string }
  | { event: "member_role_changed"; new_role: string }
  | { event: "cli_auth_approved" };
// Note: feedback uses a native PostHog survey (see openFeedbackSurvey in
// lib/posthog.ts), which auto-captures `survey shown/sent/dismissed` — no
// custom track() event needed here.

// Fire an analytics event. Safe before posthog.init() (queues / no-ops in local
// dev where there's no key), so callers never need to guard.
export function track(e: AnalyticsEvent): void {
  const { event, ...props } = e;
  posthog.capture(event, props);
}
